import React, { useState, useCallback, useMemo } from "react";
import {
  View,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  Alert,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import Text from "@components/CustomText";
import color from "@constants/color";
import Header from "../components/Header";
import TabBar from "../components/TabBar";
import SummaryStats from "../components/SummaryStats";
import DBCard from "../components/DBCard";
import AddBundleSheet from "../components/AddBundleSheet";
import UnassignSheet from "../components/UnassignSheet";
import RoutePickerSheet from "../components/RoutePickerSheet";
import MapSheet from "../components/MapSheet";
import LocalityCrateInfoTab from "./LocalityCrateInfoTab";
import useRoutingData from "../hooks/useRoutingData";
import useSummaryStats from "../hooks/useSummaryStats";
import useDbBundles from "../hooks/useDbBundles";
import useAssignDbs from "../hooks/useAssignDbs";
import useDbRoutesEdit from "../hooks/useDbRoutesEdit";
import { sharedStyles, tokens } from "../styles";

// CP Routing screen — refactored for the v2 design.
//
// DBCard surfaces:
//   • Select Routes (above)        → RoutePickerSheet (multi-select catalog)
//   • + Add Bundle (primary)       → AddBundleSheet (routes → crates → cx)
//   • View Details                 → expanded list with per-cx × unassign
//   • Unassign All                 → bulk UNASSIGN
//
// Locality / Crate / Customer info lives in LocalityCrateInfoTab.

const TABS = [
  { key: "DB", label: "DB Assigning" },
  { key: "INFO", label: "Locality/Crate Info" },
];

const CpRoutingScreen = ({ navigation }) => {
  const { data, loading, reload } = useRoutingData();
  const { submit, submitting } = useAssignDbs({ reload });
  const { saveRoutes, submitting: routesSaving } = useDbRoutesEdit({ reload });
  const summary = useSummaryStats(data);
  const bundlesByDb = useDbBundles(data);

  // Refetch on every focus so returning here from any drill-in screen
  // (PackingInterfaceScreen, etc.) reflects the latest state. Mirrors
  // PackingDbListScreen — the two entry screens share the staleness risk.
  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const [tab, setTab] = useState("DB");

  const cpDbs = useMemo(
    () => (Array.isArray(data?.cp_dbs) ? data.cp_dbs : []),
    [data],
  );
  const cpId = data?.cp_id || null;

  const enrichedDbs = useMemo(
    () =>
      cpDbs.map((db) => ({
        ...db,
        assigned_tp_count: summary.assignedByDb.get(db?.cp_db_id) || 0,
      })),
    [cpDbs, summary],
  );

  // Build a per-DB customer list (assigned customers only) so the
  // expanded DBCard can show the per-cx × unassign control.
  const customersByDb = useMemo(() => {
    const map = new Map();
    const tps = Array.isArray(data?.data?.TP) ? data.data.TP : [];
    tps.forEach((cust) => {
      const ud = (cust?.tp_list || [])[0];
      const dbId = ud?.cp_db_id;
      if (!dbId || dbId === "MISROUTE" || dbId === "UNASSIGN") return;
      if (ud?.status !== "ASSIGNED") return;
      const cxId = cust?.user_id || ud?.user_id;
      if (!cxId) return;
      const list = map.get(String(dbId)) || [];
      list.push({
        cx_id: cxId,
        user_name: cust?.user_name || ud?.user_name,
        locality: cust?.locality || ud?.locality,
        address: cust?.address,
        route_id: ud?.route_id || cust?.route_id,
        route_name: ud?.route_name || cust?.route_name,
      });
      map.set(String(dbId), list);
    });
    return map;
  }, [data]);

  const [addBundle, setAddBundle] = useState({ visible: false, db: null });
  const [unassign, setUnassign] = useState({ visible: false, db: null });
  const [picker, setPicker] = useState({ visible: false, db: null });
  const [mapState, setMapState] = useState({ visible: false, markers: [] });

  const livePickerDb = useMemo(() => {
    if (!picker.db) return null;
    return (
      enrichedDbs.find(
        (d) => String(d?.cp_db_id) === String(picker.db?.cp_db_id),
      ) || picker.db
    );
  }, [enrichedDbs, picker.db]);

  const closeAdd = useCallback(
    () => setAddBundle({ visible: false, db: null }),
    [],
  );
  const closeUnassign = useCallback(
    () => setUnassign({ visible: false, db: null }),
    [],
  );
  const closePicker = useCallback(
    () => setPicker({ visible: false, db: null }),
    [],
  );
  const closeMap = useCallback(
    () => setMapState({ visible: false, markers: [] }),
    [],
  );

  const handleAddBundle = useCallback((db) => {
    setAddBundle({ visible: true, db });
  }, []);
  const handleSelectRoutes = useCallback((db) => {
    setPicker({ visible: true, db });
  }, []);
  const handleUnassignAll = useCallback((db) => {
    setUnassign({ visible: true, db });
  }, []);

  const handleAddBundleSubmit = useCallback(
    async (payload) => {
      const ok = await submit(payload);
      if (ok) closeAdd();
    },
    [submit, closeAdd],
  );

  const handleUnassignConfirm = useCallback(async () => {
    const db = unassign.db;
    if (!db) return;
    const tps = (data?.data?.TP || [])
      .map((cust) => {
        const ud = (cust?.tp_list || [])[0];
        if (ud?.cp_db_id === db?.cp_db_id) {
          return {
            cp_id: cpId,
            cx_id: cust?.user_id || ud?.user_id,
            cp_db_id: null,
            route_id: "UNASSIGN",
          };
        }
        return null;
      })
      .filter(Boolean);
    if (!tps.length) {
      closeUnassign();
      return;
    }
    const ok = await submit({
      tps,
      cp_db_id: null,
      route_id: "UNASSIGN",
      successMessage: `Unassigned ${tps.length} customers`,
    });
    if (ok) closeUnassign();
  }, [unassign, data, cpId, submit, closeUnassign]);

  const handleUnassignCustomer = useCallback(
    (cx, db) => {
      if (!cx?.cx_id || !db?.cp_db_id) return;
      Alert.alert(
        "Unassign customer",
        `Remove ${cx.user_name || "this customer"} from ${db.db_name}?`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Unassign",
            style: "destructive",
            onPress: () =>
              submit({
                tps: [
                  {
                    cp_id: cpId,
                    cx_id: cx.cx_id,
                    cp_db_id: null,
                    route_id: "UNASSIGN",
                  },
                ],
                cp_db_id: null,
                route_id: "UNASSIGN",
                successMessage: `Unassigned ${cx.user_name || "customer"}`,
              }),
          },
        ],
      );
    },
    [cpId, submit],
  );

  const handleSaveRoutes = useCallback(
    async ({ toAdd, toRemove }) => {
      if (!livePickerDb) return false;
      const ok = await saveRoutes({
        db_id: livePickerDb.cp_db_id,
        toAdd,
        toRemove,
      });
      if (ok) closePicker();
      return ok;
    },
    [livePickerDb, saveRoutes, closePicker],
  );

  const handleViewMap = useCallback((markers) => {
    setMapState({ visible: true, markers: markers || [] });
  }, []);

  const crates = useMemo(
    () => (Array.isArray(data?.data?.CRATE) ? data.data.CRATE : []),
    [data],
  );

  return (
    <View style={sharedStyles.screen}>
      <Header title="CP Routing" onBack={() => navigation.goBack()} />
      <TabBar tabs={TABS} activeKey={tab} onChange={setTab} />
      {loading && !data ? (
        <View style={sharedStyles.centered}>
          <ActivityIndicator color={color.mediumBlack} />
        </View>
      ) : tab === "DB" ? (
        <ScrollView
          contentContainerStyle={styles.scroll}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={reload}
              tintColor={color.mediumBlack}
            />
          }
        >
          <SummaryStats
            total={summary.total}
            assigned={summary.assigned}
            remaining={summary.remaining}
            dbsFilled={summary.dbsFilled}
            totalDbs={summary.totalDbs}
          />
          {enrichedDbs.length === 0 ? (
            <Text variant="caption_2" style={sharedStyles.emptyText}>
              No delivery boys at this CP yet.
            </Text>
          ) : (
            enrichedDbs.map((db) => {
              const bundles = bundlesByDb.get(String(db.cp_db_id)) || [];
              const customers = customersByDb.get(String(db.cp_db_id)) || [];
              return (
                <DBCard
                  key={String(db.cp_db_id)}
                  db={db}
                  bundles={bundles}
                  customers={customers}
                  onPressAddBundle={handleAddBundle}
                  onPressSelectRoutes={handleSelectRoutes}
                  onPressUnassignAll={handleUnassignAll}
                  onPressUnassignCustomer={handleUnassignCustomer}
                />
              );
            })
          )}
        </ScrollView>
      ) : (
        <LocalityCrateInfoTab
          data={data}
          loading={loading}
          reload={reload}
          submitting={submitting}
          submit={submit}
          cpId={cpId}
          onPressViewMap={handleViewMap}
        />
      )}
      <AddBundleSheet
        visible={addBundle.visible}
        db={addBundle.db}
        crates={crates}
        cpId={cpId}
        submitting={submitting}
        onClose={closeAdd}
        onSubmit={handleAddBundleSubmit}
      />
      <UnassignSheet
        visible={unassign.visible}
        db={unassign.db}
        count={
          unassign.db ? summary.assignedByDb.get(unassign.db.cp_db_id) || 0 : 0
        }
        submitting={submitting}
        onClose={closeUnassign}
        onConfirm={handleUnassignConfirm}
      />
      <RoutePickerSheet
        visible={picker.visible}
        db={livePickerDb}
        cpDbs={enrichedDbs}
        saving={routesSaving}
        onClose={closePicker}
        onSave={handleSaveRoutes}
      />
      <MapSheet
        visible={mapState.visible}
        markers={mapState.markers}
        onClose={closeMap}
      />
    </View>
  );
};

export default CpRoutingScreen;

const styles = StyleSheet.create({
  scroll: {
    paddingTop: tokens.spacing.medium,
    paddingBottom: tokens.spacing.xlarge,
  },
});
